import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select, TextInput } from 'components';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import { getOrderStatusSelectOptions } from '../../OrderForm.constants';
import { TOrderFormValues } from '../../OrderForm.types';
import { TOrderFormMetaSectionProps } from './OrderFormMetaSection.types';

export const OrderFormMetaSection: React.FC<TOrderFormMetaSectionProps> = ({
  mode,
}) => {
  const localize = useLocalize();
  const { control } = useFormContext<TOrderFormValues>();
  const statusOptions = React.useMemo(
    () => getOrderStatusSelectOptions(localize),
    [localize]
  );

  return (
    <section className="admin-orders-detail__section">
      <h3 className="h6 admin-orders-detail__section-title">
        {localize('admin.orders.detail.section.identifiers')}
      </h3>
      <div className="admin-form__field-row admin-form__field-row--half">
        <div className="admin-form__field">
          <Controller
            name="id"
            control={control}
            render={({ field, fieldState }) => (
              <TextInput
                label={
                  localize('admin.orders.detail.label.internalid') as string
                }
                onChange={field.onChange}
                value={field.value ?? ''}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                disabled={mode === 'edit'}
              />
            )}
          />
        </div>
        <div className="admin-form__field">
          <Controller
            name="orderNumber"
            control={control}
            render={({ field, fieldState }) => (
              <TextInput
                label={
                  localize('admin.orders.detail.label.ordernumber') as string
                }
                onChange={field.onChange}
                value={field.value ?? ''}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                disabled
              />
            )}
          />
        </div>
      </div>
      <div className="admin-form__field-row admin-form__field-row--half">
        <div className="admin-form__field">
          <Controller
            name="status"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                label={localize('admin.orders.detail.label.status') as string}
                value={field.value}
                onChange={field.onChange}
                options={statusOptions}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
              />
            )}
          />
        </div>
      </div>
      <div className="admin-form__field-row admin-form__field-row--half">
        <div className="admin-form__field">
          <Controller
            name="totalPrice"
            control={control}
            render={({ field, fieldState }) => (
              <TextInput
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                label={localize('admin.orders.total') as string}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  field.onChange(Number.isNaN(n) ? 0 : n);
                }}
                value={field.value ?? 0}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
              />
            )}
          />
        </div>
        <div className="admin-form__field">
          <Controller
            name="userId"
            control={control}
            render={({ field, fieldState }) => (
              <TextInput
                label={localize('admin.orders.userid') as string}
                onChange={field.onChange}
                value={field.value ?? ''}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled
              />
            )}
          />
        </div>
      </div>
      <div className="admin-form__field-row admin-form__field-row--half">
        <div className="admin-form__field">
          <Controller
            name="createdAt"
            control={control}
            render={({ field, fieldState }) => (
              <TextInput
                type="datetime-local"
                label={localize('admin.orders.form.label.created') as string}
                onChange={field.onChange}
                value={field.value ?? ''}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </div>
        <div className="admin-form__field">
          <Controller
            name="updatedAt"
            control={control}
            render={({ field, fieldState }) => (
              <TextInput
                type="datetime-local"
                label={localize('admin.orders.form.label.updated') as string}
                onChange={field.onChange}
                value={field.value ?? ''}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </div>
      </div>
    </section>
  );
};
